
import { t } from '@/i18n'
import api from '@/utils/api'
import mqttSession from '@/utils/mqtt/session'
import router from '@/router'
import { Message } from 'element-ui'
import { canAccessRouteMeta } from '@/utils/authorization'
import { getStorageJson, setStorageJson } from '@/utils/storage'
import { findAccessibleTerminalEntry } from '@/utils/workspace'

let pendingMqttCredentials = null
const USER_INFO_STORAGE_KEY = 'userInfo'
const userInfoFields = [
  'token',
  'id',
  'name',
  'uname',
  'photo',
  'shop_id',
  'role_id',
  'role_type',
  'is_god',
  'is_owner',
  'permissions',
  'lasttime',
  'topic_prefix',
  'login_jti'
]

function normalizeUserInfo (payload = {}) {
  return userInfoFields.reduce((result, field) => {
    result[field] = Object.prototype.hasOwnProperty.call(payload, field) ? payload[field] : null
    return result
  }, {})
}

function saveUserInfo (state) {
  setStorageJson(USER_INFO_STORAGE_KEY, normalizeUserInfo(state))
}

function isInternalPath (path) {
  return typeof path === 'string' &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    path.split('?')[0] !== '/login'
}

function canAccessLocation (location, userInfo) {
  if (!isInternalPath(location)) return false
  const resolved = router.resolve(location)
  const route = resolved && resolved.route
  return Boolean(route && canAccessRouteMeta(userInfo, route.meta || {}, route.params || {}))
}

function getLoginTarget (redirect, userInfo) {
  if (canAccessLocation(redirect, userInfo)) return redirect
  return findAccessibleTerminalEntry(
    router,
    meta => canAccessRouteMeta(userInfo, meta),
    `/admin/edit/${userInfo.id}`
  )
}

export default {
  namespaced: true,
  state: normalizeUserInfo(getStorageJson(USER_INFO_STORAGE_KEY, {})),
  mutations: {
    // 写入用户信息
    setUser (state, payload) {
      Object.assign(state, normalizeUserInfo(payload))
      saveUserInfo(state)
    },
    updateProfile (state, { uname, photo }) {
      state.uname = uname
      state.photo = photo
      saveUserInfo(state)
    },
    // 用户信息初始化
    initUser (state) {
      if (state.token == null) {
        Object.assign(state, normalizeUserInfo(getStorageJson(USER_INFO_STORAGE_KEY, {})))
      }
    },
    // 清除用户信息
    destroyUser (state) {
      mqttSession.clear()
      Object.assign(state, normalizeUserInfo())
      localStorage.removeItem(USER_INFO_STORAGE_KEY)
    }
  },
  actions: {
    async fetchMqttCredentials ({ state }, { force = false, quiet = false } = {}) {
      if (!mqttSession.isValid(state.token)) return false
      if (!force && mqttSession.credentials && mqttSession.token === state.token) return true
      const token = state.token
      const epoch = mqttSession.epoch
      const validationEpoch = mqttSession.validationEpoch
      if (pendingMqttCredentials && pendingMqttCredentials.token === token && pendingMqttCredentials.epoch === epoch && pendingMqttCredentials.validationEpoch === validationEpoch && (!force || pendingMqttCredentials.force)) {
        return pendingMqttCredentials.promise
      }
      const request = { token, epoch, validationEpoch, force, promise: null }
      request.promise = (async () => {
        try {
          const res = await api.post('credentials', new URLSearchParams(), 'Mqtt')
          if (state.token !== token || epoch !== mqttSession.epoch || validationEpoch !== mqttSession.validationEpoch) return false
          if (Number(res.data.status) !== 1 || !mqttSession.install(res.data.mqtt, token, epoch)) {
            if (!quiet) Message.warning(t('指令服务账号未启用或配置异常，请联系管理员'))
            return false
          }
          return true
        } catch (error) {
          if (!quiet && state.token === token && epoch === mqttSession.epoch && validationEpoch === mqttSession.validationEpoch) Message.warning(t('指令服务连接配置获取失败，请稍后重试'))
          return false
        } finally {
          if (pendingMqttCredentials === request) pendingMqttCredentials = null
        }
      })()
      pendingMqttCredentials = request
      return request.promise
    },
    // 异步 清除用户信息
    async destroyUserAsync ({ commit }) {
      mqttSession.clear()
      // Revoke synchronously before any new login or pending response can run.
      commit('resetSessionData', null, { root: true })
      commit('destroyUser')
    },
    /**
     * @description: 登录
     * @param {*} params 表单.用户登录信息
     */
    async fetchLogin ({ commit }, params) {
      mqttSession.clear()
      const epoch = mqttSession.epoch
      const data = new URLSearchParams()// post对象参数 转成 字符串连接
      data.append('username', params.username)
      data.append('password', params.password)
      let res
      try {
        res = await api.post('login', data, 'Login')
      } catch (error) {
        Message.error(t('登录请求失败，请检查网络后重试'))
        return false
      }

      if (epoch !== mqttSession.epoch) return false
      if (Number(res.data.status) !== 1) {
        Message.error(t(res.data.msg || '账号或密码错误'))
        return false
      }

      const userInfo = {
        token: res.data.token,
        id: res.data.adminInfo.id,
        name: res.data.adminInfo.name,
        uname: res.data.adminInfo.uname,
        photo: res.data.adminInfo.photo,
        shop_id: res.data.adminInfo.shop_id,
        role_id: res.data.adminInfo.role_id,
        role_type: res.data.adminInfo.role_type,
        is_god: Boolean(res.data.adminInfo.is_god),
        is_owner: Boolean(res.data.adminInfo.is_owner),
        permissions: Array.isArray(res.data.adminInfo.permissions) ? res.data.adminInfo.permissions : [],
        lasttime: res.data.adminInfo.lasttime,
        topic_prefix: res.data.topic_prefix,
        login_jti: res.data.login_jti
      }

      commit('app/setLastLoginAccount', params.username, { root: true })
      commit('setUser', userInfo)
      mqttSession.install(res.data.mqtt, userInfo.token, epoch)
      const target = getLoginTarget(router.currentRoute.query.redirect, userInfo)
      try {
        await router.replace(target)
      } catch (error) {
        console.error('[User] 登录成功后页面跳转失败', error)
        Message.error(t('登录成功，但页面跳转失败，请重试'))
        return false
      }
      return true
    }
  }
}
